'use client';

import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import {
    TbBell as ConciergeBell,
    TbCalendar as CalendarDays,
    TbCamera as Camera,
    TbMap as Map,
    TbRefresh as TimerReset,
    TbSpeakerphone as Megaphone,
    TbStar as Star,
    TbToolsKitchen2 as Utensils,
    TbTrendingUp as TrendingUp,
} from 'react-icons/tb';
import type { IconType } from 'react-icons';
import type { Locale } from '@/i18n';

type SnapshotItem = {
    title: string;
    signal: string;
    question: string;
    responseTitle: string;
    response: string;
    Icon: IconType;
};

type SnapshotData = {
    eyebrow: string;
    title: string;
    body: string;
    selectorLabel: string;
    responseLabel: string;
    items: SnapshotItem[];
};

const snapshots: Record<string, Record<Locale, SnapshotData>> = {
    'food-beverage': {
        en: {
            eyebrow: 'Owner attention map',
            title: 'A restaurant page should open a sales conversation.',
            body: 'Spot the exact growth gap: appetite, visibility, ordering, or repeat visits.',
            selectorLabel: 'Pick the pressure point',
            responseLabel: 'nibras response',
            items: [
                {
                    title: 'Empty weekdays',
                    signal: 'Demand is uneven',
                    question: 'Are strong weekends hiding weak weekday traffic?',
                    responseTitle: 'Build reasons to visit before the weekend.',
                    response: 'We shape weekday offers and local targeting around real decision moments.',
                    Icon: Utensils,
                },
                {
                    title: 'Weak food photos',
                    signal: 'The menu is not selling visually',
                    question: 'Does the content make people hungry in the first three seconds?',
                    responseTitle: 'Turn signature items into campaign assets.',
                    response: 'We shoot signature dishes for appetite, texture, and delivery-app clarity.',
                    Icon: Camera,
                },
                {
                    title: 'Random posting',
                    signal: 'No clear reason to follow',
                    question: 'Does each week have a theme, offer, and story?',
                    responseTitle: 'Replace scattered posts with a content rhythm.',
                    response: 'We build content pillars and a weekly calendar so the page stays active.',
                    Icon: CalendarDays,
                },
                {
                    title: 'Ads without tracking',
                    signal: 'Budget moves, but learning does not',
                    question: 'Can the owner see which campaign creates action?',
                    responseTitle: 'Make every campaign accountable.',
                    response: 'We connect campaigns, tracking, and reporting so spend becomes a decision.',
                    Icon: TrendingUp,
                },
            ],
        },
        ar: {
            eyebrow: 'خريطة انتباه المالك',
            title: 'صفحة المطعم يجب أن تفتح محادثة بيع حقيقية.',
            body: 'حدد فجوة النمو بسرعة: الشهية، الظهور، الطلبات، أو تكرار الزيارة.',
            selectorLabel: 'اختر نقطة الضغط',
            responseLabel: 'استجابة نبراس',
            items: [
                {
                    title: 'أيام هادئة',
                    signal: 'الطلب غير متوازن',
                    question: 'هل تخفي نهاية الأسبوع القوية ضعف الحركة في باقي الأيام؟',
                    responseTitle: 'نصنع سببا واضحا للزيارة قبل نهاية الأسبوع.',
                    response: 'نصمم عروضا واستهدافا محليا حول لحظة قرار العميل الفعلية.',
                    Icon: Utensils,
                },
                {
                    title: 'صور طعام ضعيفة',
                    signal: 'القائمة لا تبيع بصريا',
                    question: 'هل يجعل المحتوى العميل يشتهي خلال أول ثلاث ثوان؟',
                    responseTitle: 'نحول الأصناف الرئيسية إلى أصول حملة.',
                    response: 'نصور الأصناف الرئيسية لتظهر شهية وواضحة في تطبيقات التوصيل.',
                    Icon: Camera,
                },
                {
                    title: 'نشر عشوائي',
                    signal: 'لا يوجد سبب واضح للمتابعة',
                    question: 'هل لكل أسبوع موضوع وعرض وقصة؟',
                    responseTitle: 'نستبدل المنشورات المتفرقة بإيقاع محتوى.',
                    response: 'نبني ركائز محتوى وتقويما أسبوعيا يبقي الصفحة نشطة.',
                    Icon: CalendarDays,
                },
                {
                    title: 'إعلانات بلا تتبع',
                    signal: 'الميزانية تتحرك بلا تعلم',
                    question: 'هل يعرف المالك أي حملة تصنع إجراء؟',
                    responseTitle: 'نجعل كل حملة قابلة للقياس.',
                    response: 'نربط الحملات بالتتبع والتقارير حتى يتحول الصرف إلى قرار واضح.',
                    Icon: TrendingUp,
                },
            ],
        },
    },
    hospitality: {
        en: {
            eyebrow: 'Guest decision map',
            title: 'Hospitality buyers choose trust before they choose a room.',
            body: 'Spot where the guest journey leaks: discovery, credibility, or booking intent.',
            selectorLabel: 'Pick the guest friction',
            responseLabel: 'nibras response',
            items: [
                {
                    title: 'Weak booking confidence',
                    signal: 'Guests hesitate before inquiry',
                    question: 'Does the page show the experience clearly enough to trust?',
                    responseTitle: 'Make the stay feel easy to imagine.',
                    response: 'We organize room and service content so the decision feels safe.',
                    Icon: ConciergeBell,
                },
                {
                    title: 'Invisible experience',
                    signal: 'The place exists, but the feeling does not',
                    question: 'Can someone understand the atmosphere before visiting?',
                    responseTitle: 'Turn the venue into a visual story.',
                    response: 'We shoot arrival, comfort, and service moments that sell the atmosphere.',
                    Icon: Camera,
                },
                {
                    title: 'Seasonal demand gaps',
                    signal: 'Offers are late or unclear',
                    question: 'Are campaigns ready before the booking window opens?',
                    responseTitle: 'Prepare offers before the market gets loud.',
                    response: 'We map seasonal packages and campaigns around real booking behavior.',
                    Icon: TimerReset,
                },
                {
                    title: 'Reputation not activated',
                    signal: 'Trust signals sit unused',
                    question: 'Are reviews, guest stories, and location strengths visible?',
                    responseTitle: 'Turn trust into conversion content.',
                    response: 'We turn reviews and guest moments into proof near booking actions.',
                    Icon: Star,
                },
            ],
        },
        ar: {
            eyebrow: 'خريطة قرار الضيف',
            title: 'عميل الضيافة يختار الثقة قبل أن يختار الغرفة.',
            body: 'حدد أين يتسرب مسار الضيف: الاكتشاف، المصداقية، أو نية الحجز.',
            selectorLabel: 'اختر عائق الضيف',
            responseLabel: 'استجابة نبراس',
            items: [
                {
                    title: 'ثقة حجز ضعيفة',
                    signal: 'الضيف يتردد قبل الاستفسار',
                    question: 'هل تعرض الصفحة التجربة بوضوح كاف لبناء الثقة؟',
                    responseTitle: 'نجعل الإقامة سهلة التخيل.',
                    response: 'ننظم محتوى الغرف والخدمة حتى يصبح القرار أكثر أمانا.',
                    Icon: ConciergeBell,
                },
                {
                    title: 'تجربة غير مرئية',
                    signal: 'المكان موجود لكن الإحساس غائب',
                    question: 'هل يفهم العميل أجواء المكان قبل الزيارة؟',
                    responseTitle: 'نحول المكان إلى قصة بصرية.',
                    response: 'نصور لحظات الوصول والراحة والخدمة التي تبيع الأجواء.',
                    Icon: Camera,
                },
                {
                    title: 'فجوات طلب موسمية',
                    signal: 'العروض متأخرة أو غير واضحة',
                    question: 'هل الحملات جاهزة قبل بداية نافذة الحجز؟',
                    responseTitle: 'نجهز العروض قبل ازدحام السوق.',
                    response: 'نرسم باقات وحملات موسمية حول سلوك الحجز الفعلي.',
                    Icon: TimerReset,
                },
                {
                    title: 'سمعة غير مفعلة',
                    signal: 'إشارات الثقة موجودة لكنها غير مستخدمة',
                    question: 'هل تظهر المراجعات وقصص الضيوف وقوة الموقع؟',
                    responseTitle: 'نحول الثقة إلى محتوى تحويل.',
                    response: 'نحول المراجعات ولحظات الضيوف إلى إثبات قرب إجراءات الحجز.',
                    Icon: Star,
                },
            ],
        },
    },
};

const fallbackSnapshots: Record<Locale, SnapshotData> = {
    en: {
        eyebrow: 'Growth snapshot',
        title: 'Your market does not need noise. It needs a clear path to action.',
        body: 'Identify the pressure point, then connect strategy, content, and performance around it.',
        selectorLabel: 'Pick the pressure point',
        responseLabel: 'nibras response',
        items: [
            {
                title: 'Unclear positioning',
                signal: 'The brand is hard to explain',
                question: 'Can prospects understand why they should choose you?',
                responseTitle: 'Clarify the message before scaling the content.',
                response: 'We define the audience, offer, and proof so execution has a clear direction.',
                Icon: Map,
            },
            {
                title: 'Weak visual proof',
                signal: 'The business looks less valuable than it is',
                question: 'Does your content show the quality customers should expect?',
                responseTitle: 'Create assets that make value visible.',
                response: 'We plan photography, video, and campaign assets around the strongest proof points.',
                Icon: Camera,
            },
            {
                title: 'No follow-up rhythm',
                signal: 'Insights do not become action',
                question: 'Does performance shape the next month?',
                responseTitle: 'Turn reporting into a next move.',
                response: 'We review results and convert recommendations into the next execution cycle.',
                Icon: Megaphone,
            },
        ],
    },
    ar: {
        eyebrow: 'لقطة نمو',
        title: 'سوقك لا يحتاج ضجيجا. يحتاج مسارا واضحا للإجراء.',
        body: 'حدد نقطة الضغط ثم اربط الاستراتيجية والمحتوى والأداء حولها.',
        selectorLabel: 'اختر نقطة الضغط',
        responseLabel: 'استجابة نبراس',
        items: [
            {
                title: 'تموضع غير واضح',
                signal: 'يصعب شرح العلامة',
                question: 'هل يفهم العميل المحتمل لماذا يختارك؟',
                responseTitle: 'نوضح الرسالة قبل تكبير المحتوى.',
                response: 'نحدد الجمهور والعرض والإثبات حتى يكون للتنفيذ اتجاه واضح.',
                Icon: Map,
            },
            {
                title: 'إثبات بصري ضعيف',
                signal: 'القيمة لا تظهر كما يجب',
                question: 'هل يعرض المحتوى الجودة التي يجب أن يتوقعها العميل؟',
                responseTitle: 'نصنع أصولا تجعل القيمة مرئية.',
                response: 'نخطط التصوير والفيديو وأصول الحملات حول أقوى نقاط الإثبات.',
                Icon: Camera,
            },
            {
                title: 'لا يوجد إيقاع متابعة',
                signal: 'الأرقام لا تتحول إلى إجراء',
                question: 'هل يشكل الأداء خطة الشهر التالي؟',
                responseTitle: 'نحول التقرير إلى خطوة تالية.',
                response: 'نراجع النتائج ونحول التوصيات إلى دورة تنفيذ جديدة.',
                Icon: Megaphone,
            },
        ],
    },
};

function getSnapshot(slug: string | null | undefined, locale: Locale) {
    return snapshots[slug || '']?.[locale] || fallbackSnapshots[locale];
}

interface IndustryGrowthSnapshotProps {
    slug?: string | null;
    title: string;
    locale: Locale;
    images?: (string | null)[];
}

export default function IndustryGrowthSnapshot({ slug, title, locale, images = [] }: IndustryGrowthSnapshotProps) {
    const isAr = locale === 'ar';
    const data = useMemo(() => getSnapshot(slug, locale), [slug, locale]);
    const [activeIndex, setActiveIndex] = useState(0);
    const active = data.items[activeIndex] || data.items[0];
    const ActiveIcon = active.Icon;
    const activeImage = images[activeIndex % Math.max(images.length, 1)];

    return (
        <section
            className="relative overflow-hidden bg-brand-bg px-4 py-24 text-brand-text md:px-20 md:py-32"
            dir={isAr ? 'rtl' : 'ltr'}
        >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_16%,rgba(255,181,62,0.24),transparent_26rem),radial-gradient(circle_at_82%_74%,rgba(234,50,40,0.22),transparent_28rem)]" />

            <div className="relative mx-auto max-w-7xl">
                <div className="mb-14 grid gap-8 lg:grid-cols-[0.78fr_1.22fr] lg:items-end">
                    <div>
                        <p className="mb-5 text-sm font-bold uppercase tracking-[0.22em] text-brand-cta">
                            {data.eyebrow}
                        </p>
                        <h2 className="font-heading text-5xl font-bold leading-none md:text-7xl">
                            {data.title}
                        </h2>
                    </div>
                    <p className="max-w-2xl border-s-4 border-brand-cta ps-6 text-lg leading-relaxed text-brand-text/76 md:text-xl">
                        {data.body}
                    </p>
                </div>

                <div className="grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
                    <div className="space-y-3">
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-text/58">
                            {data.selectorLabel}
                        </p>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                            {data.items.map((item, index) => {
                                const Icon = item.Icon;
                                const isActive = index === activeIndex;

                                return (
                                    <button
                                        key={item.title}
                                        type="button"
                                        onClick={() => setActiveIndex(index)}
                                        className={`group relative w-full overflow-hidden rounded-2xl border p-5 text-start transition-all duration-300 ${
                                            isActive
                                                ? 'border-brand-text bg-brand-text text-brand-bg shadow-[0_22px_60px_rgba(8,34,50,0.24)]'
                                                : 'border-brand-text/18 bg-white/[0.07] text-brand-text hover:border-brand-text/45 hover:bg-white/[0.1]'
                                        }`}
                                    >
                                        <span className="absolute inset-y-0 start-0 w-1 bg-brand-cta" />
                                        <span className="flex items-start gap-4">
                                            <span
                                                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${
                                                    isActive ? 'bg-brand-bg text-brand-text' : 'bg-brand-text/10 text-brand-text'
                                                }`}
                                            >
                                                <Icon className="h-5 w-5" />
                                            </span>
                                            <span>
                                                <span className="block font-heading text-2xl font-bold leading-tight">
                                                    {item.title}
                                                </span>
                                                <span
                                                    className={`mt-2 block text-sm font-bold uppercase tracking-[0.12em] ${
                                                        isActive ? 'text-brand-bg/60' : 'text-brand-text/54'
                                                    }`}
                                                >
                                                    {item.signal}
                                                </span>
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="relative overflow-hidden rounded-2xl border border-brand-text/20 bg-white/[0.08] shadow-[0_24px_80px_rgba(8,34,50,0.18)] backdrop-blur-2xl">
                        {activeImage && (
                            <div className="relative h-48 w-full overflow-hidden md:h-56">
                                <Image
                                    src={activeImage}
                                    alt={active.title}
                                    fill
                                    sizes="(max-width: 768px) 100vw, 50vw"
                                    className="object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/10 to-transparent" />
                            </div>
                        )}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active.title}
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.28, ease: 'easeOut' }}
                                className="relative p-6 md:p-8"
                            >
                                <div className="mb-6 flex items-center gap-4">
                                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand-cta text-white shadow-[0_18px_40px_rgba(234,50,40,0.24)]">
                                        <ActiveIcon className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-text/58">
                                            {data.responseLabel}
                                        </p>
                                        <h3 className="mt-2 font-heading text-2xl font-bold leading-tight md:text-3xl">
                                            {active.responseTitle}
                                        </h3>
                                    </div>
                                </div>

                                <p className="mb-2 text-sm font-bold uppercase tracking-[0.14em] text-brand-cta">
                                    {active.question}
                                </p>
                                <p className="max-w-2xl text-lg leading-relaxed text-brand-text/76">
                                    {active.response}
                                </p>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
}
